import axios from 'axios'

import router from '@/router/index'
import { generateCodeChallenge, generateCodeVerifier } from '../utils/auth'

const apiEndpoint = process.env.VUE_APP_API_ENDPOINT
const apiVersion = process.env.VUE_APP_API_VERSION

const apiClient = axios.create({
  baseURL: `${apiEndpoint}${apiVersion}`,
  withCredentials: false, // this is the default
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 1000 * 60 * 5, // 5 minutes local (not cloud)
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

apiClient.interceptors.request.use(
  (config) => {
    const accessToken = window.localStorage.getItem('access_token')

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalConfig = error.config

    if (error.response) {
      // Refresh Token was expired
      if (
        error.response.status === 400 &&
        originalConfig.url ===
          `${process.env.VUE_APP_AUTH_ENDPOINT}${process.env.VUE_APP_AUTH_TOKEN}`
      ) {
        var code_verifier = generateCodeVerifier()
        var code_challenge = generateCodeChallenge(code_verifier)

        window.localStorage.setItem('code_verifier', code_verifier)
        window.localStorage.setItem('code_challenge', code_challenge)

        const state = 'random-value'
        const code_challenge_method = 'S256'

        const authEndpoint = process.env.VUE_APP_AUTH_ENDPOINT
        const authType = process.env.VUE_APP_AUTH_TYPE
        const authResponseType = process.env.VUE_APP_AUTH_RESPONSE_TYPE
        const authClientID = process.env.VUE_APP_AUTH_CLIENT_ID

        const redirectUri =
          process.env.VUE_APP_MODE === 'development'
            ? 'http://localhost:8080/login-callback' // TODO: when upto could change to process.env.VUE_APP_AUTH_REDIRECT_URI
            : process.env.VUE_APP_AUTH_REDIRECT_URI

        // window.localStorage.removeItem('refresh_token')
        // window.localStorage.removeItem('access_token')
        window.localStorage.removeItem('company')
        window.localStorage.removeItem('user')
        window.localStorage.removeItem('authenticationStatus')

        window.location.replace(
          `${authEndpoint}${authType}?response_type=${authResponseType}&client_id=${authClientID}&state=${state}&code_challenge=${code_challenge}&code_challenge_method=${code_challenge_method}&redirect_uri=${redirectUri}`
        )
      }

      // Access Token was expired
      if (error.response.status === 401 && !originalConfig._retry) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalConfig.headers['Authorization'] = `Bearer ${token}`
              return apiClient(originalConfig)
            })
            .catch((err) => {
              return err
            })
        }

        originalConfig._retry = true
        isRefreshing = true

        const refreshToken = window.localStorage.getItem('refresh_token')

        return new Promise(function (resolve, reject) {
          apiClient({
            method: 'post',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            url: `${process.env.VUE_APP_AUTH_ENDPOINT}${process.env.VUE_APP_AUTH_TOKEN}`,
            data: `grant_type=refresh_token&client_id=${process.env.VUE_APP_AUTH_CLIENT_ID}&refresh_token=${refreshToken}`,
          })
            .then(({ data }) => {
              if (!data.error) {
                window.localStorage.setItem('refresh_token', data.refresh_token)
                window.localStorage.setItem('access_token', data.access_token)

                apiClient.defaults.headers.common[
                  'Authorization'
                ] = `Bearer ${data.access_token}`

                originalConfig.headers[
                  'Authorization'
                ] = `Bearer ${data.access_token}`

                processQueue(null, data.access_token)
                resolve(apiClient(originalConfig))
              } else {
                console.log(data.error)
              }
            })
            .catch((err) => {
              processQueue(err, null)
              reject(err)
            })
            .then(() => {
              isRefreshing = false
            })
        })

        // try {
        //   const { data } = await apiClient({
        //     method: 'post',
        //     headers: {
        //       'Content-Type': 'application/x-www-form-urlencoded'
        //     },
        //     url: `${process.env.VUE_APP_AUTH_ENDPOINT}${process.env.VUE_APP_AUTH_TOKEN}`,
        //     data: `grant_type=refresh_token&client_id=${process.env.VUE_APP_AUTH_CLIENT_ID}&refresh_token=${refreshToken}`
        //   })
        //   const { refresh_token, access_token } = data
        //   window.localStorage.setItem('refresh_token', refresh_token)
        //   window.localStorage.setItem('access_token', access_token)
        //   // apiClient.defaults.headers.Authorization = `Bearer ${access_token}`
        //   // originalConfig.headers['Authorization'] = `Bearer ${access_token}`

        //   return apiClient(originalConfig)
        // } catch (_error) {
        //   if (_error.response && _error.response.data) {
        //     return Promise.reject(_error.response.data)
        //   }
        //   return Promise.reject(_error)
        // }
      }

      // Check Network connection
      if (
        error.message &&
        error.message === 'Network Error' &&
        originalConfig.url !== '/service/hardware/sync-job'
      ) {
        axios
          .get('https://system.mastertime.io/system-status', {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          })
          .then((res) => {
            const result = res.data
            const mode =
              process.env.VUE_APP_MODE === 'development'
                ? 'dev'
                : process.env.VUE_APP_MODE
            if (result[mode].system_status_code === 0) {
              router.push({ path: '/maintenance' }).catch((err) => {})
            } else {
              router
                .push({
                  name: 'NetworkIssue',
                  params: {
                    networkIssue:
                      'INTERNET DISCONNECTED OR NETWORK WENT WRONG!',
                  },
                })
                .catch((err) => {})
            }
          })
          .catch((error) =>
            router
              .push({
                name: 'NetworkIssue',
                params: {
                  networkIssue: 'INTERNET DISCONNECTED OR NETWORK WENT WRONG!',
                },
              })
              .catch((err) => {})
          )
      }

      // Check Network server connection failed
      if (
        error.response &&
        error.response.status === 502 &&
        originalConfig.url !== '/service/hardware/sync-job'
      ) {
        axios
          .get('https://system.mastertime.io/system-status', {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          })
          .then((res) => {
            const result = res.data
            const mode =
              process.env.VUE_APP_MODE === 'development'
                ? 'dev'
                : process.env.VUE_APP_MODE
            if (result[mode].system_status_code === 0) {
              router.push({ path: '/maintenance' }).catch((err) => {})
            } else {
              router
                .push({
                  name: 'NetworkIssue',
                  params: {
                    networkIssue: 'SERVER CONNECTION FAILED',
                  },
                })
                .catch((err) => {})
            }
          })
          .catch((error) =>
            router
              .push({
                name: 'NetworkIssue',
                params: {
                  networkIssue: 'SERVER CONNECTION FAILED',
                },
              })
              .catch((err) => {})
          )
      }

      // Check Network server connection failed
      if (
        error.response &&
        error.response.status === 503 &&
        originalConfig.url !== '/service/hardware/sync-job'
      ) {
        axios
          .get('https://system.mastertime.io/system-status', {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          })
          .then((res) => {
            const result = res.data
            const mode =
              process.env.VUE_APP_MODE === 'development'
                ? 'dev'
                : process.env.VUE_APP_MODE
            if (result[mode].system_status_code === 0) {
              router.push({ path: '/maintenance' }).catch((err) => {})
            } else {
              router
                .push({
                  name: 'NetworkIssue',
                  params: { networkIssue: 'SERVER CONNECTION FAILED' },
                })
                .catch((err) => {})
            }
          })
          .catch((error) =>
            router
              .push({
                name: 'NetworkIssue',
                params: { networkIssue: 'SERVER CONNECTION FAILED' },
              })
              .catch((err) => {})
          )
      }

      // Check Network server connection timeout
      if (
        error.code &&
        error.code === 'ECONNABORTED' &&
        originalRequest.url !== '/service/hardware/sync-job'
      ) {
        axios
          .get('https://system.mastertime.io/system-status', {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          })
          .then((res) => {
            const result = res.data
            const mode =
              process.env.VUE_APP_MODE === 'development'
                ? 'dev'
                : process.env.VUE_APP_MODE
            if (result[mode].system_status_code === 0) {
              router.push({ path: '/maintenance' }).catch((err) => {})
            } else {
              router
                .push({
                  name: 'NetworkIssue',
                  params: { networkIssue: 'SERVER TIMEOUT' },
                })
                .catch((err) => {})
            }
          })
          .catch((error) =>
            router
              .push({
                name: 'NetworkIssue',
                params: { networkIssue: 'SERVER TIMEOUT' },
              })
              .catch((err) => {})
          )
      }
    }

    return Promise.reject(error)
  }
)

const accessTokens = [
  {
    id: 1,
    name_th: 'โปรแกรมบันทึกเวลา taff',
    name_en: 'taff',
    created: new Date(),
    expired: new Date(),
    permission: null,
    app: 'taff',
    revoke: false,
  },
  {
    id: 2,
    name_th: 'โปรแกรมเว็บ access',
    name_en: 'taff-access',
    created: new Date(),
    expired: new Date(),
    permission: '7 permission',
    app: 'access',
    revoke: false,
  },
  {
    id: 3,
    name_th: 'คำนวณเงินเดือน',
    name_en: 'pay',
    created: new Date(),
    expired: new Date(),
    permission: '10 permission',
    app: 'pay',
    revoke: true,
  },
]

export default {
  // AUTHENTICATION SOFTWARE 3RD PARTY
  software3rdParty(payload) {
    return apiClient.post('/auth/login/software3rdparty', payload)
  },
  // AUTHENTICATION
  login(auth) {
    return apiClient.post('/auth/login/companyadmin', auth)
  },
  changePassword({ company_uuid, account_type, account_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/${account_type}/${account_uuid}/${
        account_type === 'employee' ? 'change-password' : 'password'
      }`,
      body
    )
  },
  me() {
    return apiClient.get('/me')
  },
  fetchUiConfig(company_uuid) {
    return apiClient.get(`/ui-config/web-admin/company/${company_uuid}`)
  },
  fetchMyPolicies({ policy_type, uuid }) {
    return apiClient.get(`/my-policies/${policy_type}/${uuid}`)
  },

  // PASSWORD POLICY
  fetchPasswordPolicy(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/password-policy`, {
      params: payload?.params,
    })
  },
  editPasswordPolicy(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/password-policy`,
      payload.body
    )
  },

  // POSITION
  fetchPositions({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/position`, { params })
  },
  fetchPosition(id) {
    return apiClient.get(`/position/${id}`)
  },
  addPosition(payload) {
    return apiClient.post(
      `/company/${payload.companyUUID}/position`,
      payload.position
    )
  },
  editPosition(payload) {
    return apiClient.put(
      `/company/${payload.companyUUID}/position/${payload.positionUUID}`,
      payload.position
    )
  },
  deletePosition(payload) {
    return apiClient.delete(
      `/company/${payload.companyUUID}/position/${payload.positionUUID}`
    )
  },

  // EMPLOYEE-TYPE
  fetchEmployeeTypes({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/employee-type`, { params })
  },
  fetchEmployeeType(id) {
    return apiClient.get(`/employee-type/${id}`)
  },
  addEmployeeType(payload) {
    return apiClient.post(
      `/company/${payload.companyUUID}/employee-type`,
      payload.employeeType
    )
  },
  editEmployeeType(payload) {
    return apiClient.put(
      `/company/${payload.companyUUID}/employee-type/${payload.employeeTypeUUID}`,
      payload.employeeType
    )
  },
  deleteEmployeeType(payload) {
    return apiClient.delete(
      `/company/${payload.companyUUID}/employee-type/${payload.employeeTypeUUID}`
    )
  },

  // EMPLOYEE LEVEL
  fetchEmployeeLevels({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/employee-level`, { params })
  },
  // fetchEmployeeLevel(id) {
  //   return apiClient.get(`/company/${company_uuid}/employee-level/${id}`)
  // },
  addEmployeeLevel({ company_uuid, employeeLevel }) {
    return apiClient.post(
      `/company/${company_uuid}/employee-level`,
      employeeLevel
    )
  },
  editEmployeeLevel({ company_uuid, employee_level_uuid, employeeLevel }) {
    return apiClient.put(
      `/company/${company_uuid}/employee-level/${employee_level_uuid}`,
      employeeLevel
    )
  },
  deleteEmployeeLevel({ company_uuid, employee_level_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/employee-level/${employee_level_uuid}`
    )
  },

  // SHIFT
  fetchShifts({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/shift`, { params })
  },
  fetchShift(id) {
    return apiClient.get(`/shift/${id}`)
  },
  addShift(payload) {
    return apiClient.post(
      `/company/${payload.companyUUID}/shift`,
      payload.shift
    )
  },
  editShift(payload) {
    return apiClient.put(
      `/company/${payload.companyUUID}/shift/${payload.shiftUUID}`,
      payload.shift
    )
  },
  deleteShift(payload) {
    return apiClient.delete(
      `/company/${payload.companyUUID}/shift/${payload.shiftUUID}`
    )
  },

  // ORGANIZATION
  fetchOrganizationsAuthorizedTree({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/organization/tree`, {
      params,
    })
  },
  fetchOrganizationsAuthorized({ company_uuid, queryString }) {
    return apiClient.get(`/company/${company_uuid}/organization`, queryString)
  },
  fetchOrganizations({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/organization`, { params })
  },
  fetchOrganizationsWithQueryString({ company_uuid, queryString }) {
    return apiClient.get(`/company/${company_uuid}/organization`, queryString)
  },
  fetchOrganization({ company_uuid, organization_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/organization/${organization_uuid}`
    )
  },
  addOrganization(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/organization`,
      payload.organization
    )
  },
  editOrganization(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/organization/${payload.organization.organization_uuid}`,
      payload.organization
    )
  },
  deleteOrganization(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/organization/${payload.organization_uuid}`
    )
  },
  editOrganizationBoss(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/organization/${payload.organization_uuid}/boss`,
      payload
    )
  },
  fetchOrganizationBoss(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/organization/${payload.organization_uuid}/boss`
    )
  },
  deleteOrganizationBoss(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/organization/${payload.organization_uuid}/boss`
    )
  },

  // EMPLOYEE
  fetchEmployeeCards(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}/card`,
      {
        params: payload?.params,
      }
    )
  },
  deleteEmployeeCard(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}/card/${payload.card_uuid}`
    )
  },
  assignmentEmployeeCard(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/card-assignment`,
      payload.body
    )
  },
  deleteEmployeePhotoIdentifier({ company_uuid, employee_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/employee/${employee_uuid}/photo-identifier`
    )
  },
  fetchEmployeesAuthorized({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/employee`, { params })
  },
  importEmployeeIdentifier({ company_uuid, formData }) {
    return apiClient.post(
      `/company/${company_uuid}/import-identifier/employee`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
  },
  importEmployeeBiometric({ company_uuid, formData }) {
    return apiClient.post(
      `/company/${company_uuid}/import-biometric/employee`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
  },
  fetchTempEmployeeList(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/employee-temporary`,
      {
        params: payload?.params,
        signal: payload?.signal,
      }
    )
  },
  deleteTempEmployee(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/employee-temporary/${payload.employee_temporary_uuid}`
    )
  },
  fetchEmployees(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/employee`, {
      params: payload?.params,
      signal: payload?.signal,
    })
  },
  fetchEmployeesWithQueryString({ company_uuid, queryString }) {
    return apiClient.get(`/company/${company_uuid}/employee`, queryString)
  },
  fetchEmployee(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}`,
      {
        params: payload?.params,
      }
    )
  },
  addEmployee(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/employee`,
      payload.employee
    )
  },
  editEmployee(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}`,
      payload.employee
    )
  },
  deleteEmployee(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}`
    )
  },

  // ADMIN
  fetchAdmins(companyUUID) {
    return apiClient.get(`/company/${companyUUID}/admin`)
  },
  fetchAdmin(id) {
    return apiClient.get(`/companyadmin/${id}`)
  },
  addAdmin(payload) {
    return apiClient.post(
      `/company/${payload.companyUUID}/admin`,
      payload.admin
    )
  },
  editAdmin(payload) {
    return apiClient.put(
      `/company/${payload.companyUUID}/admin/${payload.companyAdminUUID}`,
      payload.admin
    )
  },
  editAdminActive(admin) {
    return apiClient.patch(
      `/companyadmin/${admin.company_admin_id}/is-active`,
      admin
    )
  },
  deleteAdmin(payload) {
    return apiClient.delete(
      `/company/${payload.companyUUID}/admin/${payload.companyAdminUUID}`
    )
  },

  // COMPANY
  fetchCompany(company_uuid) {
    return apiClient.get(`/company/${company_uuid}`)
  },
  editCompany({ company_uuid, company }) {
    return apiClient.put(`/company/${company_uuid}`, company)
  },
  // COMPANY LOGO
  deleteCompanyLogo(company_uuid) {
    return apiClient.delete(`/company/${company_uuid}/logo`)
  },

  // COMPANY CONFIG
  fetchTwoFactorAuthConfig(company_uuid) {
    return apiClient.get(`/company/${company_uuid}/2fa-config`)
  },
  editTwoFactorAuthConfig(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/2fa-config`,
      payload.data
    )
  },
  fetchConfigLeaveApproval(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/config/leave-approval`
    )
  },
  updateConfigLeaveApproval(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/config/leave-approval`,
      payload.data
    )
  },
  fetchConfigMicrosoftEntraId(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/config/microsoft-entra-id`
    )
  },
  updateConfigMicrosoftEntraId(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/config/microsoft-entra-id`,
      payload.data
    )
  },

  // WORKING HOURS CONFIG
  fetchConfigWorkingHours(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/config/working-hours`
    )
  },
  editConfigWorkingHours(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/config/working-hours`,
      payload.data
    )
  },
  fetchConfigWorkingHoursConstant() {
    return apiClient.get('/system-config/working-hours-constant')
  },

  // COMPANY LEAVE RIGHT
  fetchCompanyLeaveRights(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/leave-rights`, {
      params: payload?.params,
      signal: payload?.signal,
    })
  },
  editCompanyLeaveRight(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/leave-rights/${payload.leave_right_uuid}`,
      payload.data
    )
  },

  // EMPLOYEE LEAVE RIGHT
  fetchEmployeeLeaveRights(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/employee/all/leave-rights`,
      {
        params: payload?.params,
        signal: payload?.signal,
      }
    )
  },
  editEmployeeLeaveRight(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}/leave-rights`,
      payload.data
    )
  },

  // LOCATION
  fetchLocations(company_uuid) {
    return apiClient.get(`/company/${company_uuid}/location`)
  },
  fetchLocation(id) {
    return apiClient.get(`/companylocation/${id}`)
  },
  addLocation({ company_uuid, location }) {
    return apiClient.post(`/company/${company_uuid}/location`, location)
  },
  editLocation({ company_uuid, company_location_uuid, location }) {
    return apiClient.put(
      `/company/${company_uuid}/location/${company_location_uuid}`,
      location
    )
  },
  deleteLocation({ company_uuid, company_location_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/location/${company_location_uuid}`
    )
  },

  // ACCESS-TOKEN
  fetchAccessTokens() {
    return new Promise((resolve, reject) => {
      const res = {
        data: accessTokens,
      }
      setTimeout(() => {
        resolve(res)
      }, 300)
    })
  },
  revokeToken(accessToken) {
    return new Promise((resolve, reject) => {
      const res = {
        data: accessToken,
      }
      setTimeout(() => {
        resolve(res)
      }, 300)
    })
  },
  createAccessToken(accessToken) {
    return new Promise((resolve, reject) => {
      let key = Math.random().toString(36).substr(2, 12)
      accessToken.id = key
      accessToken.key = key
      const res = {
        data: accessToken,
      }
      setTimeout(() => {
        resolve(res)
      }, 300)
    })
  },

  // EMPLOYEE FEATURE TOGGLE, COMPANY FEATURE TOGGLE
  fetchCompanyFeatures(payload) {
    if (payload.is_active) {
      return apiClient.get(
        `/company-feature-toggle?company_uuid=${payload.company_uuid}&is_active=${payload.is_active}`
      )
    } else {
      return apiClient.get(
        `/company-feature-toggle?company_uuid=${payload.company_uuid}`
      )
    }
  },
  fetchEmployeeFeatures(company_uuid) {
    return apiClient.get(`/company/${company_uuid}/employee-feature-toggle`)
  },
  addEmployeeFeatureToggle(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/employee-feature-toggle`,
      payload.employee_feature_toggle
    )
  },
  deleteEmployeeFeatureToggle(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/employee-feature-toggle/${payload.employee_feature_toggle_uuid}`
    )
  },
  editEmployeeFeatureToggle(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}/employee-feature-toggle`,
      payload
    )
  },

  // USER ROLE
  addUserRole({ company_uuid, userRole }) {
    return apiClient.post(`/company/${company_uuid}/role`, userRole)
  },
  updateUserRole({ company_uuid, role_uuid, userRole }) {
    return apiClient.put(`/company/${company_uuid}/role/${role_uuid}`, userRole)
  },
  deleteUserRole({ company_uuid, role_uuid }) {
    return apiClient.delete(`/company/${company_uuid}/role/${role_uuid}`)
  },
  fetchUserRoles(company_uuid) {
    return apiClient.get(`/company/${company_uuid}/role`)
  },
  fetchUserRole({ company_uuid, role_uuid }) {
    return apiClient.get(`/company/${company_uuid}/role/${role_uuid}`)
  },
  fetchEmployeeListInRole({ company_uuid, role_uuid }) {
    return apiClient.get(`/company/${company_uuid}/role/${role_uuid}/employee`)
  },
  updateEmployeeListInRole({ company_uuid, role_uuid, data }) {
    return apiClient.put(
      `/company/${company_uuid}/role/${role_uuid}/employee`,
      data
    )
  },

  // EMPLOYEE POLICY
  updateEmployeePolicyRoles({ company_uuid, employee_uuid, data }) {
    return apiClient.put(
      `/company/${company_uuid}/employee-policy/employee/${employee_uuid}/role`,
      data
    )
  },
  fetchEmployeePolicyList(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/employee-policy`, {
      params: payload?.params,
    })
  },
  fetchEmployeePolicy({ company_uuid, employee_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/employee-policy/employee/${employee_uuid}`
    )
  },
  fetchPolicySpecification(payload) {
    return apiClient.get('/system-config/policy-spec', {
      params: payload?.params,
    })
  },
  fetchEmployeePersonalPolicy({ company_uuid, employee_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/employee-policy/employee/${employee_uuid}/personal-policy`
    )
  },
  updateEmployeePersonalPolicy({ company_uuid, employee_uuid, data }) {
    return apiClient.put(
      `/company/${company_uuid}/employee-policy/employee/${employee_uuid}/personal-policy`,
      data
    )
  },
  deleteEmployeePersonalPolicy({ company_uuid, employee_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/employee-policy/employee/${employee_uuid}/personal-policy`
    )
  },
  fetchEmployeePolicy({ company_uuid, employee_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/employee-policy/employee/${employee_uuid}`
    )
  },

  // ROLE
  fetchRoles({ companyUuid, roleType }) {
    return apiClient.get(
      `/role?company_uuid=${companyUuid}&role_type=${roleType}`
    )
  },

  // GENDER
  fetchGenders() {
    return apiClient.get('/gender')
  },

  // AREA
  fetchAreas({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/area`, { params })
  },
  fetchArea(area_uuid) {
    return apiClient.get(`/area/${area_uuid}`)
  },
  addArea(payload) {
    return apiClient.post(`/company/${payload.company_uuid}/area`, payload.area)
  },
  editArea(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/area/${payload.area_uuid}`,
      payload.area
    )
  },
  deleteArea(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/area/${payload.area_uuid}`
    )
  },

  // PREFIX
  fetchPrefixs({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/prefix`, { params })
  },
  addPrefix({ company_uuid, prefix }) {
    return apiClient.post(`/company/${company_uuid}/prefix`, prefix)
  },
  editPrefix({ company_uuid, company_prefix_uuid, prefix }) {
    return apiClient.put(
      `/company/${company_uuid}/prefix/${company_prefix_uuid}`,
      prefix
    )
  },
  deletePrefix({ company_uuid, company_prefix_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/prefix/${company_prefix_uuid}`
    )
  },

  // DOWNLOAD LOGS FILE
  generateLogsFileExcel(payload) {
    const { company_uuid, body } = payload

    return apiClient.post(
      `/company/${company_uuid}/get-activity-log/generate-excel`,
      body
    )
  },
  generateLogsFilePdf(payload) {
    const { company_uuid, body } = payload

    return apiClient.post(
      `/company/${company_uuid}/get-activity-log/generate-pdf`,
      body
    )
  },

  // DOWNLOAD TRANSACTION
  generateFileExcel({ company_uuid, body, reportType, params }) {
    return apiClient.post(
      `/company/${company_uuid}/report/${reportType}/generate-excel`,
      body,
      { params }
    )
  },
  generateFilePdf({ company_uuid, body, reportType, params }) {
    return apiClient.post(
      `/company/${company_uuid}/report/${reportType}/generate-pdf`,
      body,
      { params }
    )
  },
  jobProcess(payload) {
    return apiClient.get(`/job-process/${payload.job_process_uuid}`, {
      validateStatus: function (status) {
        return status < 400 // Resolve only if the status code is less than 400
      },
      params: payload?.params,
      signal: payload?.signal,
    })
  },
  generateFileProgress(job_process_uuid) {
    return apiClient.get(`/job-process/${job_process_uuid}`, {
      validateStatus: function (status) {
        return status < 400 // Resolve only if the status code is less than 400
      },
    })
  },
  fetchTransactionSources() {
    return apiClient.get('/transaction-source')
  },
  downloadTransaction({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/download/transaction`, body)
  },
  downloadTransactionNfc({ company_uuid, body }) {
    return apiClient.post(
      `/company/${company_uuid}/download/transaction/nfc`,
      body
    )
  },
  generateFileProgress(job_process_uuid) {
    return apiClient.get(`/job-process/${job_process_uuid}`, {
      validateStatus: function (status) {
        return status < 400 // Resolve only if the status code is less than 400
      },
    })
  },

  // UPLOAD FILE
  uploadFileImportData(payload) {
    return apiClient.post(
      `/company/${payload.uuid}/import-csv/${payload.fileType}`,
      payload.formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
  },
  uploadPayslip({ company_uuid, formData }) {
    return apiClient.post(`/company/${company_uuid}/import/payslip`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  // DOWNLOAD FILE
  downloadExampleFileImportData(payload) {
    return apiClient.get(
      `/company/${payload.uuid}/download-csv/${payload.exampleFileType}`
    )
  },

  // TIME ATTENDANCE
  fetchTimeAttendanceOffSiteGrants(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/time-attendance/off-site-grant`,
      { params: payload?.params }
    )
  },
  fetchTimeAttendanceOffSiteGrant({
    company_uuid,
    time_attendance_offsite_grant_uuid,
  }) {
    return apiClient.get(
      `/company/${company_uuid}/time-attendance/off-site-grant/${time_attendance_offsite_grant_uuid}`
    )
  },
  addTimeAttendanceOffSiteGrant(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/time-attendance/off-site-grant`,
      payload.data
    )
  },
  editTimeAttendanceOffSiteGrant(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/time-attendance/off-site-grant/${payload.time_attendance_offsite_grant_uuid}`,
      payload.data
    )
  },
  deleteTimeAttendanceOffSiteGrant(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/time-attendance/off-site-grant/${payload.time_attendance_offsite_grant_uuid}`
    )
  },

  // COMPANY LOCATION
  fetchCompanyLocations(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/company-location`, {
      params: payload?.params,
    })
  },
  addCompanyLocation(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/company-location`,
      payload.data
    )
  },
  editCompanyLocation(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/company-location/${payload.company_location_uuid}`,
      payload.data
    )
  },
  deleteCompanyLocation(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/company-location/${payload.company_location_uuid}`
    )
  },

  // OFFSITE LOCATION
  fetchOffsiteLocations(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/client-site-location`,
      {
        params: payload?.params,
      }
    )
  },
  addOffsiteLocation(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/client-site-location`,
      payload.data
    )
  },
  editOffsiteLocation(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/client-site-location/${payload.client_site_location_uuid}`,
      payload.data
    )
  },
  deleteOffsiteLocation(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/client-site-location/${payload.client_site_location_uuid}`
    )
  },

  // WORK FROM HOME LOCATION
  fetchWfhLocations(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/wfh-location`, {
      params: payload?.params,
    })
  },
  addWfhLocation(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/wfh-location`,
      payload.data
    )
  },
  editWfhLocation(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/wfh-location/${payload.wfh_location_uuid}`,
      payload.data
    )
  },
  deleteWfhLocation(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/wfh-location/${payload.wfh_location_uuid}`
    )
  },

  // LOCATION TYPE
  fetchLocationTypes(payload) {
    return apiClient.get('/system/location-type', {
      params: payload?.params,
    })
  },

  // TIME ATTENDANCE OFFSITE GRANT
  fetchTimeAttendanceOffsiteGrants(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/time-attendance-offsite-grant`,
      {
        params: payload?.params,
      }
    )
  },
  editTimeAttendanceOffsiteGrant(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/time-attendance-offsite-grant/${payload.time_attendance_offsite_grant_uuid}`,
      payload.data
    )
  },

  // OFF-SITE LOCATION
  // fetchOffSiteLocations(payload) {
  //   return apiClient.get(`/company/${payload.company_uuid}/off-site-location`, {
  //     params: payload?.params,
  //   })
  // },

  // addOffSiteLocation(payload) {
  //   return apiClient.post(
  //     `/company/${payload.company_uuid}/off-site-location`,
  //     payload.data
  //   )
  // },

  // editOffSiteLocation(payload) {
  //   return apiClient.put(
  //     `/company/${payload.company_uuid}/off-site-location/${payload.offsite_location_uuid}`,
  //     payload.data
  //   )
  // },

  // deleteOffSiteLocation(payload) {
  //   return apiClient.delete(
  //     `/company/${payload.company_uuid}/off-site-location/${payload.offsite_location_uuid}`
  //   )
  // },

  // DUTY
  fetchDuties({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/duty`, { params })
  },
  fetchDutiesMatching(companyUUID) {
    return apiClient.get(`/company/${companyUUID}/duty-matching`)
  },
  fetchDutiesType() {
    return apiClient.get('/duty-type')
  },
  addDuty(payload) {
    return apiClient.post(`/company/${payload.companyUUID}/duty`, payload.duty)
  },
  editDuty(payload) {
    return apiClient.put(
      `/company/${payload.companyUUID}/duty/${payload.companyDutyUUID}`,
      payload.duty
    )
  },
  deleteDuty(payload) {
    return apiClient.delete(
      `/company/${payload.companyUUID}/duty/${payload.companyDutyUUID}`
    )
  },
  addDutyMatching(payload) {
    return apiClient.post(
      `/company/${payload.companyUUID}/duty-matching`,
      payload.dutyMatching
    )
  },
  editDutyMatching(payload) {
    return apiClient.put(
      `/company/${payload.companyUUID}/duty-matching/${payload.companyDutyMatchingUUID}`,
      payload.dutyMatching
    )
  },
  deleteDutyMatching(payload) {
    return apiClient.delete(
      `/company/${payload.companyUUID}/duty-matching/${payload.companyDutyMatchingUUID}`
    )
  },

  // LOCK / UNLOCK
  fetchLocks({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/hardware/lock`, { params })
  },

  fetchLock({ company_uuid, hardware_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/hardware/${hardware_uuid}/lock`
    )
  },

  fetchUnlocks({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/hardware/unlock`, { params })
  },

  fetchUnlock({ company_uuid, hardware_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/hardware/${hardware_uuid}/unlock`
    )
  },

  editLock({ company_uuid, hardware_uuid, body, params }) {
    return apiClient.put(
      `/company/${company_uuid}/hardware/${hardware_uuid}/lock`,
      body,
      { params }
    )
  },

  editUnlock({ company_uuid, hardware_uuid, body, params }) {
    return apiClient.put(
      `/company/${company_uuid}/hardware/${hardware_uuid}/unlock`,
      body,
      { params }
    )
  },

  // HARDWARE 3RD PARTY
  fetchHardware3rdParties(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/hardware-3rd-party`,
      {
        params: payload?.params,
      }
    )
  },
  fetchHardware3rdParty(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/hardware-3rd-party/${payload.hardware_3rd_party_uuid}`
    )
  },
  addHardware3rdParty(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/hardware-3rd-party`,
      payload.data
    )
  },
  editHardware3rdParty(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/hardware-3rd-party/${payload.hardware_3rd_party_uuid}`,
      payload.data
    )
  },
  deleteHardware3rdParty(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/hardware-3rd-party/${payload.hardware_3rd_party_uuid}`
    )
  },

  // HARDWARE
  fetchProcessSyncJob({ company_uuid, hardware_uuid, queryString }) {
    return apiClient.get(
      `/company/${company_uuid}/hardware/${hardware_uuid}/sync-job/row/command/get_last_update`,
      queryString
    )
  },
  fetchHardwareModels(payload) {
    return apiClient.get('/hardware-model', { params: payload?.params })
  },
  fetchHardwares(payload) {
    return apiClient.get('/hardware', {
      params: payload?.params,
      signal: payload?.signal,
    })
  },
  fetchHardware(payload) {
    return apiClient.get(`/hardware/${payload.hardware_uuid}`)
  },
  editHardware(hardware) {
    return apiClient.put(`/hardware/${hardware.hardware_uuid}`, hardware)
  },
  getNetworkConfig({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/config/get-network-config`,
      body
    )
  },
  updateNetworkConfig({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/config/update-network-config`,
      body
    )
  },
  fetchDeviceInfoTranslate() {
    return apiClient.get('/system-config/device-info-translate')
  },
  fetchEmployeeInfoTranslate() {
    return apiClient.get('/system-config/employee-info-translate')
  },
  fetchEmployeeListTranslate() {
    return apiClient.get('/system-config/employee-list-translate')
  },

  // CARD
  fetchCards(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/card`, {
      params: payload?.params,
    })
  },
  addCard(payload) {
    return apiClient.post(`/company/${payload.company_uuid}/card`, payload.body)
  },
  fetchCardBySerialNumber(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/card/${payload.cardSerialNumber}`
    )
  },
  verifyCardSerialNumber(payload) {
    return apiClient.post('/verify-card-serial-number', payload.body)
  },
  editEmployeeCard(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}/card`,
      payload.card
    )
  },
  editCard(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/card/${payload.card_uuid}`,
      payload.body
    )
  },
  deleteCard(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/card/${payload.card_uuid}`
    )
  },

  // CARD MATERIAL
  fetchCardMaterials() {
    return apiClient.get('/system/card-material')
  },

  // CARD IDENTIFIER TYPE
  fetchCardIdentifierTypes() {
    return apiClient.get('/system/card-identifier-type')
  },

  // IDENTIFY MODE
  fetchIdentifyMode({ hardware_model }) {
    return apiClient.get(`/hardware/model/${hardware_model}/identify-mode`)
  },
  fetchIdentifyModes() {
    return apiClient.get('/identify-mode')
  },
  editIdentifyMode(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/hardware/model/${payload.hardwareModel}/identify-mode`,
      payload.body
    )
  },

  // CARD TYPE
  fetchCardTypes(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/card-type`, {
      params: payload?.params,
    })
  },
  addCardType(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/card-type`,
      payload.body
    )
  },
  editCardType(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/card-type/${payload.card_type_uuid}`,
      payload.body
    )
  },
  deleteCardType(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/card-type/${payload.card_type_uuid}`
    )
  },

  // HARDWARE CONNECTION
  fetchCodes(hardware_model_code) {
    return apiClient.get(
      `/system/sync-code?hardware_model_code=${hardware_model_code}`
    )
  },
  // SYNC JOBS, JOB CANCEL
  cancelJob(hardware_uuid) {
    return apiClient.post(`/service/hardware/${hardware_uuid}/sync-job/cancel`)
  },
  // SYNC JOBS, JOB
  fetchJobs({ params }) {
    return apiClient.get('/service/hardware/sync-job', { params })
  },
  syncLogUnionSyncJob(payload) {
    return apiClient.get(`/service/hardware/sync-log-union-sync-job`, {
      params: payload?.params,
    })
  },
  fetchJob(sync_job_uuid) {
    return apiClient.get(`/service/hardware/sync-job/${sync_job_uuid}`)
  },
  // DEVICE COMMANDS
  employeeLastUpdated(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/employee/${payload.employee_uuid}/last-updated`
    )
  },

  getLastUpdate({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/get_last_update`,
      body
    )
  },
  setFirmware({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/set_firmware`,
      body
    )
  },
  getDeviceInfo({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/get_device_info`,
      body
    )
  },
  getDeviceLog({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/get_device_log`,
      body
    )
  },
  getEmployee({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/get_employee`,
      body
    )
  },
  getEmployeeList({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/get_employee_list`,
      body
    )
  },
  getAccessAuthorityList({ company_uuid, hardware_model, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model}/command/get_access_authority_list`,
      body
    )
  },
  userAcknowledge(sync_job_uuid) {
    return apiClient.post(
      `/service/hardware/sync-job/${sync_job_uuid}/acknowledge`
    )
  },

  // BIOMETRIC
  fetchBiometrics() {
    return apiClient.get(`/biometric`)
  },
  fetchEmployeeBiometric({ company_uuid, employee_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/employee/${employee_uuid}/biometric`
    )
  },
  updateEmployeeBiometric({
    company_uuid,
    employee_uuid,
    biometric_property_code,
    body,
  }) {
    return apiClient.put(
      `/company/${company_uuid}/employee/${employee_uuid}/biometric/${biometric_property_code}`,
      body
    )
  },
  deleteEmployeeBiometric({
    company_uuid,
    employee_uuid,
    biometric_property_code,
  }) {
    return apiClient.delete(
      `/company/${company_uuid}/employee/${employee_uuid}/biometric/${biometric_property_code}`
    )
  },

  // COMMAND
  fetchDeviceCommands(company_uuid) {
    return apiClient.get(`/system/sync-code?company_uuid=${company_uuid}`)
  },
  fetchDevices({ company_uuid, available_for_sync_code }) {
    return apiClient.get(
      `/hardware?company_uuid=${company_uuid}&available_for_sync_code=${available_for_sync_code}`
    )
  },
  createDeviceJob({ company_uuid, hardware_model_code, sync_code, body }) {
    return apiClient.post(
      `/company/${company_uuid}/hardware/model/${hardware_model_code}/command/${sync_code}`,
      body
    )
  },

  resetEmployeePassword({ company_uuid, employee_uuid }) {
    return apiClient.post(
      `/company/${company_uuid}/employee/${employee_uuid}/reset-password`
    )
  },

  // HOLIDAY
  fetchHoliday({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/holiday`)
  },
  addHoliday({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/holiday`, body)
  },
  editHoliday({ company_uuid, holiday_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/holiday/${holiday_uuid}`,
      body
    )
  },
  deleteHoliday({ company_uuid, holiday_uuid }) {
    return apiClient.delete(`/company/${company_uuid}/holiday/${holiday_uuid}`)
  },
  fetchHolidayTypes() {
    return apiClient.get('/holiday-type')
  },

  // TA RIGHTS GROUP
  fetchTaRightsGroup({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/ta/authority`)
  },
  addTaRightGroup({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/ta/authority`, body)
  },
  editTaRightGroup({ company_uuid, authority_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/ta/authority/${authority_uuid}`,
      body
    )
  },
  deleteTaRightGroup({ company_uuid, authority_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/ta/authority/${authority_uuid}`
    )
  },

  // ACCESS RIGHTS GROUP
  fetchRightsGroup({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/access/authority`)
  },
  addRightGroup({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/access/authority`, body)
  },
  editRightGroup({ company_uuid, authority_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/access/authority/${authority_uuid}`,
      body
    )
  },
  deleteRightGroup({ company_uuid, authority_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/access/authority/${authority_uuid}`
    )
  },

  // TA HARDWARE GROUP
  fetchTaHardwaresGroup({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/ta/hardware-group`)
  },
  fetchTaHardwareGroupById({ company_uuid, hardware_group_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/ta/hardware-group/${hardware_group_uuid}`
    )
  },
  addTaHardwareGroup({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/ta/hardware-group`, body)
  },
  editTaHardwareGroup({ company_uuid, hardware_group_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/ta/hardware-group/${hardware_group_uuid}`,
      body
    )
  },
  deleteTaHardwareGroup({ company_uuid, hardware_group_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/ta/hardware-group/${hardware_group_uuid}`
    )
  },
  fetchTaHardwaresGroupMember({ company_uuid, hardware_group_uuid, query }) {
    return apiClient.get(
      `/company/${company_uuid}/ta/hardware-group/${hardware_group_uuid}/hardware`,
      { params: query }
    )
  },
  addTaHardwareGroupMember({ company_uuid, hardware_group_uuid, body }) {
    return apiClient.post(
      `/company/${company_uuid}/ta/hardware-group/${hardware_group_uuid}/hardware`,
      body
    )
  },
  deleteTaHardwareGroupMember({
    company_uuid,
    hardware_group_uuid,
    hardware_uuid,
  }) {
    return apiClient.delete(
      `/company/${company_uuid}/ta/hardware-group/${hardware_group_uuid}/hardware/${hardware_uuid}`
    )
  },

  // HARDWARE GROUP
  fetchHardwaresGroup({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/access/hardware-group`, {
      params,
    })
  },
  fetchHardwareGroupById({ company_uuid, hardware_group_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/access/hardware-group/${hardware_group_uuid}`
    )
  },
  addHardwareGroup({ company_uuid, body }) {
    return apiClient.post(
      `/company/${company_uuid}/access/hardware-group`,
      body
    )
  },
  editHardwareGroup({ company_uuid, hardware_group_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/access/hardware-group/${hardware_group_uuid}`,
      body
    )
  },
  deleteHardwareGroup({ company_uuid, hardware_group_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/access/hardware-group/${hardware_group_uuid}`
    )
  },
  fetchHardwaresGroupMember(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/hardware-group/${payload.hardware_group_uuid}/hardware`,
      { params: payload?.params }
    )
  },
  addHardwareGroupMember(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/access/hardware-group/${payload.hardware_group_uuid}/hardware`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },
  deleteHardwareGroupMember(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/access/hardware-group/${payload.hardware_group_uuid}/hardware`,
      {
        data: {
          hardware_uuids: [payload.hardware_uuid],
        },
        params: payload?.params,
      }
    )
  },

  // TA EMPLOYEE GROUP
  fetchTaEmployeesGroup({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/ta/employee-group`)
  },
  fetchTaEmployeeGroupById({ company_uuid, employee_group_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/ta/employee-group/${employee_group_uuid}`
    )
  },
  addTaEmployeeGroup({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/ta/employee-group`, body)
  },
  editTaEmployeeGroup({ company_uuid, employee_group_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/ta/employee-group/${employee_group_uuid}`,
      body
    )
  },
  deleteTaEmployeeGroup({ company_uuid, employee_group_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/ta/employee-group/${employee_group_uuid}`
    )
  },
  fetchTaEmployeesGroupMember({ company_uuid, employee_group_uuid, query }) {
    return apiClient.get(
      `/company/${company_uuid}/ta/employee-group/${employee_group_uuid}/employee`,
      { params: query }
    )
  },
  addTaEmployeeGroupMember({ company_uuid, employee_group_uuid, body }) {
    return apiClient.post(
      `/company/${company_uuid}/ta/employee-group/${employee_group_uuid}/employee`,
      body
    )
  },
  deleteTaEmployeeGroupMember({
    company_uuid,
    employee_group_uuid,
    employee_uuid,
  }) {
    return apiClient.delete(
      `/company/${company_uuid}/ta/employee-group/${employee_group_uuid}/employee/${employee_uuid}`
    )
  },

  // EMPLOYEE GROUP
  fetchEmployeesGroup({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/access/employee-group`, {
      params,
    })
  },
  fetchEmployeeGroupById({ company_uuid, employee_group_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/access/employee-group/${employee_group_uuid}`
    )
  },
  addEmployeeGroup({ company_uuid, body }) {
    return apiClient.post(
      `/company/${company_uuid}/access/employee-group`,
      body
    )
  },
  editEmployeeGroup({ company_uuid, employee_group_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/access/employee-group/${employee_group_uuid}`,
      body
    )
  },
  deleteEmployeeGroup({ company_uuid, employee_group_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/access/employee-group/${employee_group_uuid}`
    )
  },
  fetchEmployeesGroupMember(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/employee-group/${payload.employee_group_uuid}/employee`,
      { params: payload?.params }
    )
  },
  addEmployeeGroupMember(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/access/employee-group/${payload.employee_group_uuid}/employee`,
      payload.body,
      { params: payload?.params }
    )
  },
  deleteEmployeeGroupMember(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/access/employee-group/${payload.employee_group_uuid}/employee`,
      {
        data: {
          employee_uuids: [payload.employee_uuid],
        },
        params: payload?.params,
      }
    )
  },

  // EMPLOYEE TIME-ATTENDANCE
  editEmployeeTimeAttendance({ company_uuid, employee_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/employee/${employee_uuid}/ta-employee`,
      body
    )
  },

  // TIMEZONE
  fetchTimezones({ company_uuid, params }) {
    return apiClient.get(`/company/${company_uuid}/timezone`, { params })
  },
  addTimezone({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/timezone`, body)
  },
  editTimezone({ company_uuid, timezone_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/timezone/${timezone_uuid}`,
      body
    )
  },
  deleteTimezone({ company_uuid, timezone_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/timezone/${timezone_uuid}`
    )
  },

  // REPORT
  fetchReports101({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-101`, body, {
      params,
    })
  },
  fetchReports102({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-102`, body, {
      params,
    })
  },
  fetchReports103({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-103`, body, {
      params,
    })
  },
  fetchReports104({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-104`, body, {
      params,
    })
  },
  fetchReports105({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-105`, body, {
      params,
    })
  },
  fetchReports106({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-106`, body, {
      params,
    })
  },
  fetchReports107({ company_uuid, body, params }) {
    return apiClient.post(`/company/${company_uuid}/report/report-107`, body, {
      params,
    })
  },

  workTimeReprocess({ company_uuid, body, params }) {
    return apiClient.post(
      `/company/${company_uuid}/work-time/reprocess`,
      body,
      { params }
    )
  },

  // LOGS
  getActivityLogs({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/get-activity-log`, body)
  },

  // FIRMWARE
  fetchFirmwares() {
    return apiClient.get('/system/firmware')
  },

  // LEAVE TYPE
  fetchLeaveTypes(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/leave-type`, {
      params: payload?.params,
    })
  },

  addLeaveType({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/leave-type`, body)
  },

  editLeaveType({ company_uuid, leave_type_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/leave-type/${leave_type_uuid}`,
      body
    )
  },

  deleteLeaveType({ company_uuid, leave_type_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/leave-type/${leave_type_uuid}`
    )
  },

  // LEAVE APPROVER
  fetchLeaveApprovers(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/leave-approver`, {
      params: payload?.params,
    })
  },
  fetchLeaveApprover(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/leave-approver/employee/${payload.employee_uuid}`,
      {
        params: payload?.params,
      }
    )
  },

  editLeaveApproverEmployees(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/leave-approver/employee/${payload.employee_uuid}`,
      payload.body
    )
  },

  // LEAVE FILE
  fetchLeaveRecords(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/leave-record/request/export`,
      {
        params: payload?.params,
      }
    )
  },

  leaveFileImport(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/leave-record/request/import`,
      payload.formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
  },

  leaveFileExport(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/leave-record/request/export`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },

  leaveFileImportExample({ company_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/leave-record/request/import/format`
    )
  },

  // LEAVE TENURE POLICY
  fetchConfigLeaveTenurePolicy({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/config/leave-tenure-policy`)
  },
  editConfigLeaveTenurePolicy({ company_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/config/leave-tenure-policy`,
      body
    )
  },

  // CONFIG LEAVE PERIOD
  fetchConfigLeavePeriodStartDate(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/config/leave-period-start-date`,
      {
        params: payload?.params,
      }
    )
  },
  editConfigLeavePeriodStartDate({ company_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/config/leave-period-start-date`,
      body
    )
  },

  // ANNOUNCEMENT
  fetchAnnouncements(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/announcement`, {
      params: payload?.params,
    })
  },
  addAnnouncement(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/announcement`,
      payload.body
    )
  },
  editAnnouncement(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/announcement/${payload.announcement_uuid}`,
      payload.body
    )
  },
  deleteAnnouncement(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/announcement/${payload.announcement_uuid}`
    )
  },
  sendAnnouncement(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/announcement/${payload.announcement_uuid}/send`,
      payload.body
    )
  },

  // ACCESS AUTHORITY
  fetchAccessAuthorities(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/access/authority`, {
      params: payload?.params,
    })
  },
  fetchAccessAuthority({ company_uuid, authority_uuid }) {
    return apiClient.get(
      `/company/${company_uuid}/access/authority/${authority_uuid}`
    )
  },
  addAccessAuthority({ company_uuid, body }) {
    return apiClient.post(`/company/${company_uuid}/access/authority`, body)
  },
  deleteAccessAuthority({ company_uuid, authority_uuid }) {
    return apiClient.delete(
      `/company/${company_uuid}/access/authority/${authority_uuid}`
    )
  },
  editAccessAuthority({ company_uuid, authority_uuid, body }) {
    return apiClient.put(
      `/company/${company_uuid}/access/authority/${authority_uuid}`,
      body
    )
  },
  fetchAuthorityDeviceSummary(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/authority-hardware/summary`,
      {
        params: payload?.params,
      }
    )
  },
  fetchAuthorityEmployeeSummary(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/authority-employee/summary`,
      {
        params: payload?.params,
      }
    )
  },
  fetchAuthorityEmployeeInAuthority(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/authority-employee/authority`,
      {
        params: payload?.params,
      }
    )
  },
  fetchAuthorityHardwareInAuthority(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/authority-hardware/authority`,
      {
        params: payload?.params,
      }
    )
  },
  fetchAuthorityDeviceMember(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/authority/${payload.authority_uuid}/authority-hardware`,
      {
        params: payload?.params,
      }
    )
  },
  fetchAuthorityEmployeeMember(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/access/authority/${payload.authority_uuid}/authority-employee`,
      {
        params: payload?.params,
      }
    )
  },
  addAuthorityDeviceMember(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/access/authority/${payload.authority_uuid}/authority-hardware`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },
  addAuthorityEmployeeMember(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/access/authority/${payload.authority_uuid}/authority-employee`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },
  deleteAuthorityDeviceMember(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/access/authority/${payload.authority_uuid}/authority-hardware`,
      {
        data: payload.body,
        params: payload?.params,
      }
    )
  },
  deleteAuthorityEmployeeMember(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/access/authority/${payload.authority_uuid}/authority-employee`,
      {
        data: payload.body,
        params: payload?.params,
      }
    )
  },

  // WEBHOOK
  fetchWebhookSubscriptions(payload) {
    return apiClient.get(
      `/company/${payload.company_uuid}/webhook/subscription`,
      {
        params: payload?.params,
      }
    )
  },
  fetchWebhookCompanyReference(payload) {
    return apiClient.get(`/webhook/company-reference/${payload.company_uuid}`, {
      params: payload?.params,
    })
  },
  fetchWebhookTopics(payload) {
    return apiClient.get('/webhook/topic', {
      params: payload?.params,
    })
  },
  addWebhookSubscription(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/webhook/subscription`,
      payload.body
    )
  },
  editWebhookSubscription(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/webhook/subscription/${payload.subscription_uuid}`,
      payload.body
    )
  },
  deleteWebhookSubscription(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/webhook/subscription/${payload.subscription_uuid}`
    )
  },
  fetchWebhookDeveloperEmails(payload) {
    return apiClient.get(`/company/${payload.company_uuid}/webhook/developer`, {
      params: payload?.params,
    })
  },
  addWebhookDeveloperEmail(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/webhook/developer`,
      payload.body
    )
  },
  editWebhookDeveloperEmail(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/webhook/developer/${payload.developer_uuid}`,
      payload.body
    )
  },
  deleteWebhookDeveloperEmail(payload) {
    return apiClient.delete(
      `/company/${payload.company_uuid}/webhook/developer/${payload.developer_uuid}`
    )
  },
  testPingWebhook(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/webhook/subscription/${payload.subscription_uuid}/ping`
    )
  },
  webhookResendTransaction(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/webhook/resend-transaction`,
      payload.body
    )
  },

  // FUNCTION
  addEmployeeToAuthorities(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/function/employee-to-authority`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },
  editEmployeeToAuthorities(payload) {
    return apiClient.put(
      `/company/${payload.company_uuid}/function/employee-to-authority`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },
  getTransactions(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/transaction/realtime-snapshot`,
      payload.body,
      {
        params: payload?.params,
      }
    )
  },
}
